import React, { useMemo } from 'react';

import { useReactNativeProps, runningInRNWebView, PendingCardToken } from '@eika-infrastruktur/mobilbank-lib';

import { useCreditCardsList } from '@/domain/card';
import {
    AppleWalletService,
    PendingCardTokensList,
    useEligibleCreditCardsQueryForApple,
} from '@/domain/digital-wallet';
import { usePendingCards } from '@/domain/digital-wallet/hooks/query/use-pending-cards';
import { useMyDebitCards } from '@/react-query/hooks/query/use-debit-cards';

import { CreditCardWalletType } from '@generated-types';

import { PendingCardTokensContent } from './Content';

import './PendingCardTokens.scss';

type PendingCardsQueryProps = {
    pendingTokens: PendingCardToken[];
};

const PendingCardsKbQuery = ({ pendingTokens }: PendingCardsQueryProps) => {
    const pendingCardsQuery = usePendingCards(true);
    const debitCardsQuery = useMyDebitCards({
        enabled: true,
    });

    const creditCardsQuery = useCreditCardsList();
    const eligibleCardsQuery = useEligibleCreditCardsQueryForApple({
        select: (data) => data.filter((card) => card.walletType === CreditCardWalletType.APPLE_PAY),
    });

    if (
        pendingCardsQuery.isPending ||
        pendingCardsQuery.isError ||
        debitCardsQuery.isPending ||
        debitCardsQuery.isError ||
        creditCardsQuery.isPending ||
        creditCardsQuery.isError ||
        eligibleCardsQuery.isPending ||
        eligibleCardsQuery.isError
    ) {
        return null;
    }

    const pendingCardTokens: PendingCardTokensList = [
        ...AppleWalletService.pairKbPendingTokens(creditCardsQuery.data, eligibleCardsQuery.data, pendingTokens),
        ...AppleWalletService.pairTePendingTokens(debitCardsQuery.data, pendingCardsQuery.data, pendingTokens),
    ];

    return <PendingCardTokensContent pendingCardTokens={pendingCardTokens} />;
};

const PendingCardTokens = () => {
    const nativeProps = useReactNativeProps();

    const pendingTokens = nativeProps.appleWallet?.pendingCardTokens || [];
    const hasPendingTokens = pendingTokens.length > 0;
    const enabled = useMemo(
        () => runningInRNWebView && Boolean(nativeProps.supports?.applePay) && hasPendingTokens,
        [hasPendingTokens, nativeProps.supports?.applePay],
    );

    if (!enabled) {
        return false;
    }

    return <PendingCardsKbQuery pendingTokens={pendingTokens} />;
};

export default PendingCardTokens;
